import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies, max-lines -- Login page orchestrates multiple auth flows (magic link, password, forgot password, confirmations) and composes desktop/mobile layouts */
import { Box, Button, Link } from "@mui/material";

import { ThemeProvider } from "@mui/material/styles";
import { ensureCognitoUser } from "@src/appV2/Auth/cognito/cognitoAuth";
import { requestPasswordReset } from "@src/appV2/Auth/cognito/cognitoPasswordAuth";
import { initCognitoSession } from "@src/appV2/Auth/cognito/initCognitoSession";
import { isSafeRedirectUrl } from "@src/appV2/Auth/cognito/isSafeRedirectUrl";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { isPasskeySupported, signInWithPasskey } from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { getRememberedEmail } from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { requestRememberEmail } from "@src/appV2/Auth/rememberEmail/requestRememberEmail";
import {
  IDENTITY_PROVIDER,
  LOGIN_OUTCOME,
  logPasswordResetRequestFailure,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { MOBILE_SSO_CODE_VERIFIER_KEY } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { OutageBanner } from "@src/appV2/OutageBanner/OutageBanner";
import { useOutageBanner } from "@src/appV2/OutageBanner/useOutageBanner";
import { useIsDesktop } from "@src/appV2/redesign/AppLayout/useResponsive";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { berlinRebrandTheme } from "@src/appV2/redesign/theme/theme";
import { loginWithEmailAndPassword } from "@src/mobile/Login/loginWithEmailAndPassword";
import { sendLoginEmail } from "@src/mobile/Login/sendLoginEmail";
import { type ReactElement, useCallback, useEffect, useState } from "react";

// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk after password login
import { useDispatch } from "react-redux";

import { useHistory, useLocation } from "react-router-dom";
import loginIllustration from "./assets/login-illustration.webp";
import { DesktopLoginLayout } from "./DesktopLoginLayout";
import { LoginForm } from "./LoginForm";
import { LoginWithEmailAndPasswordForm } from "./LoginWithEmailAndPasswordForm";
import { MobileLoginLayout } from "./MobileLoginLayout";
import { useResendCooldown } from "./useResendCooldown";
import { useSsoLogin } from "./useSsoLogin";
const WORKPLACE_SIGNUP_URL = "https://www.clipboardworks.com/workplaces/signup";
type LoginMode = "magicLink" | "password" | "forgotPassword" | "resetEmailSent";

const VALID_LOGIN_MODES: readonly LoginMode[] = [
  "magicLink",
  "password",
  "forgotPassword",
  "resetEmailSent",
];

function parseInitialLoginMode(search: string): LoginMode {
  const mode = new URLSearchParams(search).get("mode");
  return VALID_LOGIN_MODES.find((m) => m === mode) ?? "magicLink";
}

/**
 * Clears stale OAuth/PKCE state from previous failed SSO attempts so the
 * login page starts clean. Only clears OAuth flow state — not Cognito
 * tokens or session data, which are managed by the logout flow.
 */
function clearStaleAuthState(): void {
  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState") || key?.includes("oauthPKCE")) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem(MOBILE_SSO_CODE_VERIFIER_KEY);
}

export function Login(): ReactElement {
  const { tu } = useTu("login");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const history = useHistory();
  const location = useLocation();
  // Pre-fill the email remembered on this device, if the user opted in before.
  const [email, setEmail] = useState(() => getRememberedEmail() ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [loginMode, setLoginMode] = useState<LoginMode>(() =>
    parseInitialLoginMode(location.search)
  );

  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetError, setResetError] = useState<string | undefined>();
  const [magicLinkError, setMagicLinkError] = useState<string | undefined>();
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | undefined>();

  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });
  const passkeySupported = isPasskeySupported();

  // Clear stale auth state when the login page mounts. Previous failed
  // SSO attempts or stale Cognito tokens can leave state that causes
  // infinite loading loops or redirect errors on retry.
  useEffect(() => {
    clearStaleAuthState();
  }, []);

  const { ssoMode, handleSsoRedirect } = useSsoLogin(email);
  const { secondsRemaining, isCoolingDown, startCooldown } = useResendCooldown();

  // Sends the magic link to the current email and starts the resend cooldown on
  // success. Shared by the initial submit and the confirmation-screen resend;
  // returns whether the send succeeded so callers can react (e.g. show the
  // confirmation screen).
  const sendMagicLink = useCallback(
    async (options?: { isResend?: boolean }) => {
      setLoading(true);
      setMagicLinkError(undefined);

      const result = await sendLoginEmail({
        email,
        requestedURL: localStorage.getItem("redirectURL") ?? undefined,
        // The initial send already JIT-imported the user; a resend shouldn't
        // re-trigger the PreSignUp Lambda (DEVOP-5526).
        skipEnsureUser: options?.isResend ?? false,
      });

      if (result.success) {
        startCooldown();
      } else {
        setMagicLinkError(result.error);
      }

      setLoading(false);
      return result.success;
    },
    [email, startCooldown]
  );

  const handleMagicLinkSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    setPasskeyError(undefined);

    // Production: SSO replaces magic link for CBH employees
    if (ssoMode === "sso_only") {
      setLoading(true);
      setMagicLinkError(undefined);
      await handleSsoRedirect("submit");
      setLoading(false);
      return;
    }

    if (await sendMagicLink()) {
      setEmailSent(true);
    }
  }, [email, ssoMode, handleSsoRedirect, sendMagicLink]);

  // Resends the magic link to the same email from the confirmation screen.
  // Gated by a cooldown so users can't spam the endpoint; the cooldown
  // restarts on every successful resend.
  const handleResendEmail = useCallback(async () => {
    if (!email || isCoolingDown) {
      return;
    }

    await sendMagicLink({ isResend: true });
  }, [email, isCoolingDown, sendMagicLink]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);
    setLoginError(undefined);

    const result = await loginWithEmailAndPassword({ email, password });

    if (result.success) {
      // Request the post-sign-in "remember your email?" prompt (non-blocking;
      // the root-level manager shows it after we redirect).
      requestRememberEmail(email);

      // Hydrate Redux session so PrivateRoute sees a valid session immediately,
      // matching what AuthRedirect does for the magic link flow.
      const sessionRestored = await dispatch(initCognitoSession());
      if (!sessionRestored) {
        setLoginError(tu("Something went wrong. Please try again."));
        setLoading(false);
        return;
      }

      const storedRedirectUrl = localStorage.getItem("redirectURL");
      const redirectUrl =
        storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
      localStorage.removeItem("redirectURL");
      history.replace(redirectUrl);
      return;
    }

    setLoginError(result.error);
    setLoading(false);
  }, [email, password, dispatch, history, tu]);

  // Reaches `signInWithPasskey` synchronously from the click (no prior await)
  // so the WebAuthn ceremony stays inside the user gesture.
  const handlePasskeyClick = useCallback(async () => {
    if (!email) {
      return;
    }

    setPasskeyLoading(true);
    setPasskeyError(undefined);
    setMagicLinkError(undefined);
    setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSKEY);
    logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_STARTED, { hasEmail: Boolean(email) });

    // Cognito's user-existence-error prevention fabricates a challenge for
    // unknown emails (sometimes factor selection → "no-passkey", sometimes a
    // real WEB_AUTHN ceremony, sometimes a NotAuthorized/UserNotFound error), so
    // the client genuinely can't tell "no account", "account without a passkey",
    // and "wrong passkey" apart. Show one honest message for every passkey
    // failure instead of leaking a misleading user-existence claim.
    const passkeyUnavailableMessage = tu(
      "We couldn't sign you in with a passkey for this email. If you have an account, sign in with a link or password, then add a passkey from the My Account page."
    );

    try {
      const result = await signInWithPasskey(email.trim());

      if (result.status === "no-passkey") {
        logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_NO_CREDENTIAL);
        setPasskeyError(passkeyUnavailableMessage);
        setPasskeyLoading(false);
        return;
      }

      // Request the post-sign-in "remember your email?" prompt (non-blocking).
      requestRememberEmail(email);

      const sessionRestored = await dispatch(initCognitoSession({ authMethod: "passkey" }));
      if (!sessionRestored) {
        setPasskeyError(tu("Something went wrong. Please try again."));
        setPasskeyLoading(false);
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PASSKEY_LOGIN_SUCCESS, { outcome: LOGIN_OUTCOME.SUCCESS });
      const storedRedirectUrl = localStorage.getItem("redirectURL");
      const redirectUrl =
        storedRedirectUrl && isSafeRedirectUrl(storedRedirectUrl) ? storedRedirectUrl : "/";
      localStorage.removeItem("redirectURL");
      history.replace(redirectUrl);
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "passkey");
      logError(APP_V2_APP_EVENTS.PASSKEY_LOGIN_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      // A user-not-found error here is just prevention noise — the email may
      // well be valid. Collapse it into the neutral message; keep genuine
      // passkey errors (cancelled, security, unsupported) actionable.
      setPasskeyError(
        mapped.code === "auth/user-not-found" ? passkeyUnavailableMessage : mapped.message
      );
      setPasskeyLoading(false);
    }
  }, [email, dispatch, history, tu]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) {
      setResetError(tu("Please enter your email address first."));
      return;
    }

    setLoading(true);
    setResetError(undefined);

    try {
      await ensureCognitoUser(email);
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      setLoginMode("resetEmailSent");
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "password");
      logPasswordResetRequestFailure({ error, metadata: { errorCode: mapped.code } });
      setResetError(mapped.message);
    } finally {
      setLoading(false);
    }
  }, [email, tu]);

  // Clear any stale passkey error when the email changes, so a "no passkey for
  // this email" message doesn't linger against a different address.
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setPasskeyError(undefined);
  }, []);

  const isDesktop = useIsDesktop();
  const outageBanner = useOutageBanner();
  const banner = outageBanner.isVisible ? <OutageBanner /> : undefined;

  const showIllustration =
    !(loginMode === "magicLink" && emailSent) && loginMode !== "resetEmailSent";

  const showWelcomeHeading = (loginMode === "magicLink" && !emailSent) || loginMode === "password";

  const formBox = (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {loginMode === "magicLink" && !emailSent && (
        <LoginForm
          email={email}
          isSsoEmail={ssoMode === "sso_only"}
          loading={loading}
          showSsoButton={ssoMode === "sso_and_magic_link"}
          errorMessage={magicLinkError}
          showPasskeyButton={isPasskeyAuthEnabled && passkeySupported && ssoMode !== "sso_only"}
          passkeyLoading={passkeyLoading}
          passkeyError={passkeyError}
          setEmail={handleEmailChange}
          onSsoClick={() => handleSsoRedirect("sso_button")}
          onSubmit={handleMagicLinkSubmit}
          onPasskeyClick={() => {
            void handlePasskeyClick();
          }}
          onSwitchToPassword={() => {
            setMagicLinkError(undefined);
            setPasskeyError(undefined);
            setLoginMode("password");
          }}
        />
      )}

      {loginMode === "magicLink" && emailSent && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Verification link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("We have sent an email with a login link to {{email}}.", {
              values: { email },
            })}
          </Text>
          {magicLinkError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {magicLinkError}
            </Text>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              width: "100%",
            }}
          >
            <LoadingButton
              fullWidth
              variant="primary"
              size="large"
              isLoading={loading}
              disabled={isCoolingDown}
              onClick={() => {
                void handleResendEmail();
              }}
            >
              {isCoolingDown
                ? tu("Resend email in {{seconds}}s", { values: { seconds: secondsRemaining } })
                : tu("Resend email")}
            </LoadingButton>
            <Button
              variant="text"
              disabled={loading}
              sx={{ textTransform: "none", color: "primary.main" }}
              onClick={() => {
                setMagicLinkError(undefined);
                setEmailSent(false);
              }}
            >
              {tu("Go back to login page")}
            </Button>
          </Box>
        </Box>
      )}

      {loginMode === "password" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <LoginWithEmailAndPasswordForm
            email={email}
            password={password}
            loading={loading}
            errorMessage={loginError}
            setEmail={setEmail}
            setPassword={setPassword}
            onSubmit={handlePasswordSubmit}
            onForgotPassword={() => {
              setLoginError(undefined);
              setLoginMode("forgotPassword");
            }}
          />
          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              alignSelf: "stretch",
              gap: 5,
              marginTop: 4,
            }}
          >
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
            <Text variant="body2" sx={{ color: "base.inactive" }}>
              {tu("or")}
            </Text>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                backgroundColor: "border.base",
              }}
            />
          </Box>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              marginTop: 4,
              alignSelf: "center",
            }}
            onClick={() => {
              setLoginMode("magicLink");
            }}
          >
            {tu("Sign in with link")}
          </Button>
        </Box>
      )}

      {loginMode === "forgotPassword" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 4,
            width: "100%",
          }}
        >
          <Text bold variant="body1" sx={{ textAlign: "center" }}>
            {tu("Set/reset password")}
          </Text>
          <Text variant="body2" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu("Enter your email address and we'll send you a link to set/reset your password.")}
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              {tu("Email")}
            </Text>
            <TextInput
              fullWidth
              required
              type="email"
              placeholder="your.email@example.com"
              value={email}
              disabled={loading}
              autoComplete="email"
              inputProps={{ "aria-label": "Email" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "1rem",
                  backgroundColor: "background.standout",
                },
              }}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Box>
          {resetError && (
            <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
              {resetError}
            </Text>
          )}
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={loading}
            disabled={!email}
            onClick={() => {
              void handleForgotPassword();
            }}
          >
            {tu("Send Set/Reset Link")}
          </LoadingButton>

          <Button
            variant="text"
            disabled={loading}
            sx={{
              textTransform: "none",
              color: "primary.main",
              alignSelf: "center",
            }}
            onClick={() => {
              setResetError(undefined);
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {loginMode === "resetEmailSent" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
            <Text bold variant="h5" sx={{ color: "base.base" }}>
              {tu("Reset link sent")}
            </Text>
          </Box>
          <Text bold variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
            {tu(
              "We have sent a password reset link to {{email}}. Check your email and follow the instructions.",
              { values: { email } }
            )}
          </Text>
          <Button
            variant="text"
            sx={{ textTransform: "none", color: "primary.main" }}
            onClick={() => {
              setLoginMode("password");
            }}
          >
            {tu("Back to sign in")}
          </Button>
        </Box>
      )}

      {showIllustration && (
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Text variant="body2" sx={{ color: "base.muted" }}>
            {tu("Don't have a workplace account?")}
          </Text>
          <Link
            href={WORKPLACE_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: "0.875rem", color: "primary.main" }}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.LOGIN_SIGNUP_LINK_CLICKED);
            }}
          >
            {tu("Sign up")}
          </Link>
        </Box>
      )}
    </Box>
  );

  if (isDesktop) {
    return (
      <ThemeProvider theme={berlinRebrandTheme}>
        <DesktopLoginLayout banner={banner}>
          {showIllustration && (
            <img
              src={loginIllustration}
              alt="Person sitting at a desk"
              style={{ height: "min(212px, 22vh)", width: "auto" }}
            />
          )}
          {showWelcomeHeading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <Text
                variant="h5"
                sx={{
                  fontSize: "2rem",
                  lineHeight: 1.2,
                  color: "base.base",
                  textAlign: "center",
                }}
              >
                {tu("Welcome back")}
              </Text>
            </Box>
          )}
          {formBox}
        </DesktopLoginLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={berlinRebrandTheme}>
      <MobileLoginLayout banner={banner}>
        {showIllustration && (
          <img
            src={loginIllustration}
            alt="Person sitting at a desk"
            style={{ height: 170, width: "auto", marginBottom: 40 }}
          />
        )}
        {formBox}
      </MobileLoginLayout>
    </ThemeProvider>
  );
}
/* eslint-enable import/max-dependencies, max-lines */
